import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { handleInvalidToken } from "~/utils/apiError";
import {
  productPassesDietaryFilters,
  type DietaryFilter,
} from "~/utils/effectiveAllergens";

// --------------- Interfaces ---------------

export interface ProductCategory {
  id: string | number;
  name: string;
  is_active: boolean;
  biz: boolean;
  products: Product[];
}

export interface Product {
  id: string | number;
  name: string;
  description?: string;
  is_active: boolean;
  is_hot?: boolean;
  price_gross?: number;
  price_net?: number;
  price_vat?: number;
  attachments?: Record<string, unknown>;
  allergens?: { name: string }[];
  same_day?: boolean;
  future_day?: boolean;
  min_order_amount?: number;
  sku?: string;
  category_id?: string | number;
  type?: "Inventory" | "Bundle";
  is_premium?: boolean;
  unified_category?: string;
  [key: string]: unknown;
}

export interface Tag {
  id: string | number;
  name: string;
  type?: string;
  category?: string;
  is_active: boolean;
  is_linked: boolean;
}

export interface Allergen {
  id: string | number;
  name: string;
}

export interface Cuisine {
  id: string | number;
  name: string;
}

export const useProductsStore = defineStore("products", () => {
  // --------------- State ---------------
  const productCategories = ref<ProductCategory[] | false>(false);
  const filteredProductCategories = ref<ProductCategory[] | false>(false);
  const bundles = ref<Product[] | false>(false);
  const chosenProduct = ref<Product | false>(false);
  const tags = ref<Tag[] | false>(false);
  const allergens = ref<Allergen[] | false>(false);
  const cuisines = ref<Cuisine[] | false>(false);
  const searchResults = ref<unknown[] | false>(false);
  const dietaryFilters = ref<DietaryFilter[] | false>(false);

  // The Set Menus carousel, through the same chips as every card. A bundle passes no dietary or
  // allergen chip, so the carousel empties while one is on.
  const visibleBundles = computed(() =>
    Array.isArray(bundles.value)
      ? bundles.value.filter((b) => productPassesDietaryFilters(b, dietaryFilters.value))
      : [],
  );

  // --------------- Setters ---------------

  function setProductCategories(payload: ProductCategory[]) {
    productCategories.value = payload
      .filter(
        (cat) =>
          cat.is_active &&
          cat.biz &&
          cat.products.length > 0 &&
          cat.products.some((p) => p.is_active),
      )
      .map((cat) => ({
        ...cat,
        products: cat.products.map((p) => ({
          ...p,
          is_hot: p.is_hot || false,
        })),
      }));
  }

  // Dietary/allergen filtering is delegated to productPassesDietaryFilters so the
  // store page, the order-edit page and the premium filter all agree with the badges
  // rendered by ProductAllergens.vue. It matches on tags and flags only — the previous
  // name/description substring scan excluded any product whose text merely mentioned an
  // allergen word ("doughnuts" contains "nuts", "Free From Gluten" contains "gluten").
  function setFilteredProductCategories(payload: DietaryFilter[] | false) {
    dietaryFilters.value = payload;
    // Only ever hold filtered ProductCategory[] (or false). If there's no filter, or
    // categories haven't loaded yet, store false rather than casting the filter payload
    // into this ref — consumers map it as ProductCategory[] and would crash otherwise.
    if (!payload || !productCategories.value) {
      filteredProductCategories.value = false;
      return;
    }

    filteredProductCategories.value = productCategories.value.map((cat) => ({
      ...cat,
      products: (cat.products || []).filter((product) =>
        productPassesDietaryFilters(product as unknown as Record<string, unknown>, payload),
      ),
    }));
  }

  function setBundles(payload: ProductCategory[] | Product[]) {
    if (!Array.isArray(payload)) {
      bundles.value = false;
      return;
    }
    bundles.value = (payload as ProductCategory[]).flatMap((cat) => {
      if (!Array.isArray(cat.products) || cat.products.length === 0) return [];
      return cat.products.filter((p) => p.is_active && p.type === "Bundle");
    });
  }

  function setChosenProduct(payload: Product | false) {
    chosenProduct.value = payload;
  }

  // --------------- Actions ---------------

  async function getTags() {
    const config = useRuntimeConfig();
    try {
      const response = await $fetch<{ tags: Tag[] }>(
        `${config.public.apiBase}/api/public/tags?limit=9999&filter=${encodeURIComponent(
          JSON.stringify([
            { key: "is_linked", value: 1 },
            { key: "is_active", value: 1 },
          ]),
        )}`,
      );
      tags.value = response.tags;
      await getAllergens();
    } catch {
      // Error
    }
  }

  async function getAllergens() {
    const config = useRuntimeConfig();
    try {
      const response = await $fetch<{ allergens: Allergen[] }>(
        `${config.public.apiBase}/api/public/allergens`,
      );
      allergens.value = response.allergens;
      await getCuisines();
    } catch {
      // Error
    }
  }

  async function getCuisines() {
    const config = useRuntimeConfig();
    try {
      const response = await $fetch<{ models: Cuisine[] }>(
        `${config.public.apiBase}/api/public/cuisines?limit=9999&filter=${encodeURIComponent(
          JSON.stringify([
            { key: "is_linked", value: 1 },
            { key: "is_active", value: 1 },
          ]),
        )}`,
      );
      cuisines.value = response.models;
    } catch {
      // Error
    }
  }

  async function getProductById(id: string, orderId?: string | number) {
    const config = useRuntimeConfig();
    try {
      // With an orderId (group order), read through the authenticated, menu-filtered
      // endpoint so the order's frozen rules prune the product's modifier options
      // server-side (or 404 it if the rules removed it). Without one, the public read.
      // Same method — "just authenticated or not."
      const response = orderId
        ? await useAuthenticatedFetch().authFetch<Product>(
            `/api/customers/products/${id}?orderId=${orderId}`,
          )
        : await $fetch<Product>(`${config.public.apiBase}/api/public/products/${id}`);
      chosenProduct.value = response;
      return response;
    } catch (error: unknown) {
      // Only the authenticated (orderId) read can hit a token error; a public
      // read must never trigger a logout.
      if (orderId) handleInvalidToken(error);
      return null;
    }
  }

  async function getProductBySlug(slug: string) {
    const config = useRuntimeConfig();
    try {
      const response = await $fetch<Product>(
        `${config.public.apiBase}/api/public/products/slugs/${slug}`,
      );
      chosenProduct.value = response;
    } catch {
      // Error
    }
  }

  async function getProductsByMaker(id: string | number) {
    const config = useRuntimeConfig();
    try {
      const response = await $fetch<{ products: Product[] }>(
        `${config.public.apiBase}/api/public/products/maker/${id}?limit=999`,
      );
      setBundles(response.products as unknown as ProductCategory[]);
    } catch {
      // Error
    }
  }

  return {
    // State
    productCategories,
    filteredProductCategories,
    bundles,
    visibleBundles,
    chosenProduct,
    tags,
    allergens,
    cuisines,
    searchResults,

    // Setters
    setProductCategories,
    setFilteredProductCategories,
    setBundles,
    setChosenProduct,

    // Actions
    getTags,
    getAllergens,
    getCuisines,
    getProductById,
    getProductBySlug,
    getProductsByMaker,
  };
});
